import moment from 'moment'

/**
 * The only field {@link getProgramPlanExpiryLabel} reads — spelled `| undefined`
 * explicitly for `exactOptionalPropertyTypes`, so both a `PatientPackage` (where
 * the field is a required `string`) and a bare `{ ValidUntilDateTimeUtc }` test
 * fixture satisfy it.
 */
type PlanExpiry = {
  ValidUntilDateTimeUtc?: string | null | undefined
}

/**
 * `Expires Jul 28, 2027` — the plan's own end-of-validity date, the copy the
 * plan chip shows on hover (QMD-4145).
 *
 * Formatted in UTC, not local time: `ValidUntilDateTimeUtc` arrives at midnight
 * UTC, so a clinician west of Greenwich reading it in local time would see the
 * day before. The date the backend sent is the date the plan expires, so it is
 * rendered as sent. Parsed strictly as ISO 8601 — the contract's shape — so a
 * value that is not a date answers `null` rather than falling through moment's
 * `Date()` guesswork. `null` when the plan carries no usable expiry: there is
 * then nothing to hover for and the chip is left bare.
 */
export function getProgramPlanExpiryLabel(programPackage: PlanExpiry | null | undefined): string | null {
  const validUntil = programPackage?.ValidUntilDateTimeUtc

  if (!validUntil) {
    return null
  }

  const expiry = moment.utc(validUntil, moment.ISO_8601)

  return expiry.isValid() ? `Expires ${expiry.format('MMM D, YYYY')}` : null
}
